//CRN_20260707 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { catchError, from, interval, Observable, of, startWith, switchMap, takeWhile, throwError } from 'rxjs';
import { CompanyStatementDTO } from 'src/app/core/models/system/statement/company-statement.dto';
import { ConfigSystemParams, Duration, eSimApiPaths } from 'src/app/core/consts';
import { ConfigSystemService } from '../config-system/config-system-service';
import { EncryptionService } from '../../general/encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from '../../general/exceptions-service';
import { HttpClientService } from '../../general/http-client-service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StatementBatchResultDTO } from 'src/app/core/models/system/statement/statement-batch-result.dto';
import { StatementExistsForMonthDTO } from 'src/app/core/models/system/statement/statement-exists-for-month.dto';
import { StatementScope } from 'src/app/core/enums';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';

@Injectable({ providedIn: 'root' })

export class StatementService {

  constructor(
    private configSystemService: ConfigSystemService,
    private encryptionService  : EncryptionService,
    private exceptionsService  : ExceptionsService,
    private httpClientService  : HttpClientService
  ) { }

  private buildFallbackFileName(companyID: number,
                                year     : number,
                                month    : number,
                                scope    : StatementScope=StatementScope.CompanyOnly,
                                extension: string='pdf',
                                versionNo: number | undefined=undefined): string {
    try {
      const paddedMonth =month.toString().padStart(2, '0');
      const resellerPart=scope==StatementScope.Hierarchy ? '-Reseller' : '';
      const versionPart =(versionNo ?? 0)>0 ? '-v' + versionNo : '';

      return 'statement-' + year + '-' + paddedMonth + '-' + Math.abs(companyID ?? 0) + resellerPart + versionPart + '.' + extension;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'statement.' + (extension ?? 'pdf');
    }
  }

  public downloadCompanyStatement(companyID       : number,
                                  year            : number,
                                  month           : number,
                                  scope           : StatementScope=StatementScope.CompanyOnly,
                                  versionNo       : number | undefined=undefined,
                                  statementIdEncSS: string | undefined=undefined): Observable<HttpResponse<Blob>> {
    try {
      if (companyID==undefined || Math.abs(companyID)<=0)
        return throwError(() => 'StatementService.downloadCompanyStatement() - The company cannot be determined.');

      if (!year || !month)
        return throwError(() => 'StatementService.downloadCompanyStatement() - The statement period cannot be determined.');

      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>Math.abs(companyID), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.getBlob(environment.eSimApi
                                                                  + eSimApiPaths.Statements
                                                                  + "/"
                                                                  + eSimApiPaths.StatementsGetCompanyStatement
                                                                  + "?companyIdEncSS=" + companyIdEncSS
                                                                  + "&year="           + year
                                                                  + "&month="          + month
                                                                  + "&scope="          + scope
                                                                  + this.statementIdParam(statementIdEncSS))
                                   .pipe(
                                      catchError((error: HttpErrorResponse) => {
                                        return from(this.parseBlobError(error))
                                               .pipe(switchMap(message => throwError(() => message)));
                                      })
                                   );
                         }),
                switchMap((response: HttpResponse<Blob>) => {
                            this.triggerDownload(response, companyID, year, month, false, scope, 'pdf', versionNo);

                            return of(response);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public downloadStatement(companyID       : number,
                           year            : number,
                           month           : number,
                           scope           : StatementScope=StatementScope.CompanyOnly,
                           versionNo       : number | undefined=undefined,
                           openInTab       : boolean=true,
                           statementIdEncSS: string | undefined=undefined): Observable<HttpResponse<Blob>> {
    try {
      if (companyID==undefined || companyID<=0)
        return throwError(() => 'StatementService.downloadStatement() - The company cannot be determined.');

      if (!year || !month)
        return throwError(() => 'StatementService.downloadStatement() - The statement period cannot be determined.');

      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyID, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.getBlob(environment.eSimApi
                                                                  + eSimApiPaths.Statements
                                                                  + "/"
                                                                  + eSimApiPaths.StatementsDownloadStatement
                                                                  + "?companyIdEncSS=" + companyIdEncSS
                                                                  + "&year="           + year
                                                                  + "&month="          + month
                                                                  + "&scope="          + scope
                                                                  + this.statementIdParam(statementIdEncSS))
                                   .pipe(
                                      catchError((error: HttpErrorResponse) => {
                                        return from(this.parseBlobError(error))
                                               .pipe(switchMap(message => throwError(() => message)));
                                      })
                                   );
                         }),
                switchMap((response: HttpResponse<Blob>) => {
                            this.triggerDownload(response, companyID, year, month, openInTab, scope, 'pdf', versionNo);

                            return of(response);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public downloadStatementCsv(companyID       : number,
                              year            : number,
                              month           : number,
                              scope           : StatementScope=StatementScope.CompanyOnly,
                              versionNo       : number | undefined=undefined,
                              statementIdEncSS: string | undefined=undefined): Observable<HttpResponse<Blob>> {
    try {
      if (companyID==undefined || companyID<=0)
        return throwError(() => 'StatementService.downloadStatementCsv() - The company cannot be determined.');

      if (!year || !month)
        return throwError(() => 'StatementService.downloadStatementCsv() - The statement period cannot be determined.');

      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyID, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.getBlob(environment.eSimApi
                                                                  + eSimApiPaths.Statements
                                                                  + "/"
                                                                  + eSimApiPaths.StatementsDownloadStatementCsv
                                                                  + "?companyIdEncSS=" + companyIdEncSS
                                                                  + "&year="           + year
                                                                  + "&month="          + month
                                                                  + "&scope="          + scope
                                                                  + this.statementIdParam(statementIdEncSS))
                                   .pipe(
                                      catchError((error: HttpErrorResponse) => {
                                        return from(this.parseBlobError(error))
                                               .pipe(switchMap(message => throwError(() => message)));
                                      })
                                   );
                         }),
                switchMap((response: HttpResponse<Blob>) => {
                            this.triggerDownload(response, companyID, year, month, false, scope, 'csv', versionNo);

                            return of(response);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public generateStatement(companyID: number,
                           year     : number,
                           month    : number,
                           scope    : StatementScope=StatementScope.CompanyOnly): Observable<CompanyStatementDTO> {
    try {
      if (companyID==undefined || companyID<=0)
        return throwError(() => 'StatementService.generateStatement() - The company cannot be determined.');

      if (!year || !month)
        return throwError(() => 'StatementService.generateStatement() - The statement period cannot be determined.');

      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyID, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService._post<CompanyStatementDTO>(environment.eSimApi
                                                                                     + eSimApiPaths.Statements
                                                                                     + "/"
                                                                                     + eSimApiPaths.StatementsGenerateStatement
                                                                                     + "?companyIdEncSS=" + companyIdEncSS
                                                                                     + "&year="           + year
                                                                                     + "&month="          + month
                                                                                     + "&scope="          + scope,
                                                                                     null);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public generateStatementsForHierarchy(year       : number,
                                        month      : number,
                                        missingOnly: boolean): Observable<StatementBatchResultDTO> {
    try {
      if (!year || !month)
        return throwError(() => 'StatementService.generateStatementsForHierarchy() - The statement period cannot be determined.');

      return this.httpClientService._post<StatementBatchResultDTO>(environment.eSimApi
                                                                  + eSimApiPaths.Statements
                                                                  + "/"
                                                                  + eSimApiPaths.StatementsGenerateStatementsForHierarchy
                                                                  + "?year="        + year
                                                                  + "&month="       + month
                                                                  + "&missingOnly=" + missingOnly,
                                                                  null);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getStatementBatchStatus(jobId: string): Observable<StatementBatchResultDTO> {
    try {
      if (jobId==undefined || jobId.trim()=="")
        return throwError(() => 'StatementService.getStatementBatchStatus() - The job cannot be determined.');

      return this.httpClientService.get<StatementBatchResultDTO>(environment.eSimApi
                                                                 + eSimApiPaths.Statements
                                                                 + "/"
                                                                 + eSimApiPaths.StatementsGetStatementBatchStatus
                                                                 + "?jobId=" + encodeURIComponent(jobId));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getStatementBatchStatusUpdates(jobId: string): Observable<StatementBatchResultDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableStatementBatchMilliSecs, Duration._5SecMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getStatementBatchStatus(jobId)),
                   takeWhile((result: StatementBatchResultDTO) => this.statementBatchIsRunning(result), true)
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getStatementVersions(companyID: number,
                              year     : number,
                              month    : number,
                              scope    : StatementScope=StatementScope.CompanyOnly): Observable<CompanyStatementDTO[]> {
    try {
      if (companyID==undefined || Math.abs(companyID)<=0)
        return throwError(() => 'StatementService.getStatementVersions() - The company cannot be determined.');

      if (!year || !month)
        return throwError(() => 'StatementService.getStatementVersions() - The statement period cannot be determined.');

      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>Math.abs(companyID), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.get<CompanyStatementDTO[]>(environment.eSimApi
                                                                                     + eSimApiPaths.Statements
                                                                                     + "/"
                                                                                     + eSimApiPaths.StatementsGetStatementVersions
                                                                                     + "?companyIdEncSS=" + companyIdEncSS
                                                                                     + "&year="           + year
                                                                                     + "&month="          + month
                                                                                     + "&scope="          + scope);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public statementExistsKey(companyID: number, scope: StatementScope): string {
    try {
      return Math.abs(companyID ?? 0) + "_" + scope;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public statementBatchIsRunning(result: StatementBatchResultDTO | undefined): boolean {
    try {
      const status=(result?.status ?? "").toLowerCase();

      return status != "complete" && status != "failed";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private async parseBlobError(error: HttpErrorResponse): Promise<string> {
    try {
      const contentType=(error.headers?.get('content-type') ?? '').toLowerCase();

      if (contentType.startsWith('application/json') && error.error instanceof Blob) {
        const text   =await error.error.text();
        const parsed =<{ errorMessage?: string; error?: string; auditId?: number }>JSON.parse(text);
        const message=parsed?.errorMessage ?? parsed?.error;

        if (message)
          return typeof parsed?.auditId==='number' ? (message + ' (audit ID: ' + parsed.auditId + ')') : message;
      }

      switch (error.status) {
        case 400:
          return 'StatementService - The requested statement period is invalid.';
        case 403:
          return 'StatementService - You are not authorised to view this version of the statement.';
        case 404:
          return 'StatementService - No statement was found for the selected company and period.';
        default:
          return 'StatementService - The statement could not be generated. Please try again.';
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'StatementService - The statement could not be generated.';
    }
  }

  private statementIdParam(statementIdEncSS: string | undefined): string {
    try {
      if (statementIdEncSS==undefined || statementIdEncSS.trim()=="")
        return "";

      return "&statementIdEncSS=" + encodeURIComponent(statementIdEncSS);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public statementExistsForMonth(year : number,
                                 month: number): Observable<StatementExistsForMonthDTO> {
    try {
      if (!year || !month)
        return throwError(() => 'StatementService.statementExistsForMonth() - The statement period cannot be determined.');

      return this.httpClientService.get<StatementExistsForMonthDTO>(environment.eSimApi
                                                                    + eSimApiPaths.Statements
                                                                    + "/"
                                                                    + eSimApiPaths.StatementsStatementExistsForMonth
                                                                    + "?year="  + year
                                                                    + "&month=" + month);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  private triggerDownload(response : HttpResponse<Blob>,
                          companyID: number,
                          year     : number,
                          month    : number,
                          openInTab: boolean=false,
                          scope    : StatementScope=StatementScope.CompanyOnly,
                          extension: string='pdf',
                          versionNo: number | undefined=undefined): void {
    try {
      const body=response.body;

      if (!body)
        throw new Error('StatementService.triggerDownload() - The response contained no statement data.');

      const disposition=response.headers?.get('content-disposition') ?? '';
      const matched    =/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const fileName   =matched?.[1] ?? this.buildFallbackFileName(companyID, year, month, scope, extension, versionNo);
      const objectUrl  =URL.createObjectURL(body);

      if (openInTab) {
        window.open(objectUrl, '_blank');
      } else {
        const anchor=document.createElement('a');

        anchor.href    =objectUrl;
        anchor.download=fileName;
        anchor.click();
      }

      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
